import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, doc, updateDoc, addDoc, deleteDoc, orderBy } from './firestore-compat';
import AddHomecareTaskModal from './AddHomecareTaskModal';
import './TarefasHomecare.css';

/** Formata "YYYY-MM-DD" → "DD/MM/YYYY" */
function formatDateBR(dateStr) {
    if (!dateStr) return '--';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
}

/** Formata ISO string de timestamp em "DD/MM/YYYY HH:mm" */
function formatTsBR(isoStr) {
    if (!isoStr) return '--';
    return new Date(isoStr).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TAREFAS_HOMECARE_DESATIVADO = true;

function TarefasHomecare() {
    const [tasks, setTasks] = useState([]);
    const [systemUsers, setSystemUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [isAlarmActive, setIsAlarmActive] = useState(false);
    const [showAlarmPopup, setShowAlarmPopup] = useState(false);
    const [acknowledgedLateTaskIds, setAcknowledgedLateTaskIds] = useState([]);
    const [currentTime, setCurrentTime] = useState(new Date());
    
    // Filtros
    const [filterExecutante, setFilterExecutante] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, pendente, feito
    
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (TAREFAS_HOMECARE_DESATIVADO) return;
        // Load users
        const unsubUsers = onSnapshot(query(collection(db, 'users'), orderBy('name')), (snap) => {
            setSystemUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Load tasks
        const unsubTasks = onSnapshot(query(collection(db, 'tarefas_homecare')), (snap) => {
            setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubUsers(); unsubTasks(); };
    }, []);

    // Alarm Checker loop
    useEffect(() => {
        const interval = setInterval(() => {
            let shouldAlarm = false;
            const now = new Date();
            
            const currentLateTasks = [];
            tasks.forEach(t => {
                if (t.status !== 'Feito' && t.data && t.horario) {
                    const slaDate = new Date(`${t.data}T${t.horario}:00`);
                    if (now > slaDate) {
                        shouldAlarm = true;
                        currentLateTasks.push(t);
                    }
                }
            });

            setIsAlarmActive(shouldAlarm);

            // Verifica se há novas tarefas atrasadas não reconhecidas
            const unacknowledged = currentLateTasks.filter(t => !acknowledgedLateTaskIds.includes(t.id));
            if (unacknowledged.length > 0) {
                setShowAlarmPopup(true);
            }

        }, 10000); // Check every 10s
        
        return () => clearInterval(interval);
    }, [tasks, acknowledgedLateTaskIds]);

    // Timer loop for countdowns
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleDismissAlarm = () => {
        const currentLateIds = tasks.filter(t => isTaskLate(t)).map(t => t.id);
        setAcknowledgedLateTaskIds(prev => [...new Set([...prev, ...currentLateIds])]);
        setShowAlarmPopup(false);
    };

    const handleSaveTask = async (taskData) => {
        if (editingTask) {
            const updates = { ...taskData };
            
            // Verifica se houve ajuste no SLA (mudou data ou horario)
            if (taskData.data !== editingTask.data || taskData.horario !== editingTask.horario) {
                // Se ainda não tiver um SLA original gravado, este é o primeiro ajuste
                if (!editingTask.slaOriginal) {
                    updates.slaOriginal = {
                        data: editingTask.data,
                        horario: editingTask.horario
                    };
                }
                updates.slaAjustadoPor = currentUser ? (currentUser.displayName || currentUser.email) : 'desconhecido';
                updates.slaAjustadoEm = new Date().toISOString();
            }

            await updateDoc(doc(db, 'tarefas_homecare', editingTask.id), updates);
        } else {
            await addDoc(collection(db, 'tarefas_homecare'), {
                ...taskData,
                status: 'Pendente',
                createdAt: new Date().toISOString(),
                createdBy: currentUser ? currentUser.uid : 'desconhecido'
            });
        }
        setIsModalOpen(false);
    };

    const handleToggleStatus = async (task) => {
        const newStatus = task.status === 'Feito' ? 'Pendente' : 'Feito';
        await updateDoc(doc(db, 'tarefas_homecare', task.id), { status: newStatus });
    };

    const handleDeleteTask = async (id) => {
        if(window.confirm("Deseja excluir esta tarefa?")) {
            await deleteDoc(doc(db, 'tarefas_homecare', id));
        }
    }

    const openNewModal = () => {
        setEditingTask(null);
        setIsModalOpen(true);
    };

    // Computar KPIs
    const kpis = {
        urgente_importante: tasks.filter(t => t.urgencyLevel === 'urgente_importante' && t.status !== 'Feito').length,
        nao_urgente_importante: tasks.filter(t => t.urgencyLevel === 'nao_urgente_importante' && t.status !== 'Feito').length,
        urgente_nao_importante: tasks.filter(t => t.urgencyLevel === 'urgente_nao_importante' && t.status !== 'Feito').length,
        nao_urgente_nao_importante: tasks.filter(t => t.urgencyLevel === 'nao_urgente_nao_importante' && t.status !== 'Feito').length,
    };

    const getExecutorName = (id) => {
        const u = systemUsers.find(user => user.id === id);
        return u ? u.name : 'Desconhecido';
    };

    // Aplicar Filtros
    let filteredTasks = tasks;
    if (filterExecutante) {
        filteredTasks = filteredTasks.filter(t => t.executante === filterExecutante);
    }
    if (filterStatus === 'pendente') {
        filteredTasks = filteredTasks.filter(t => t.status !== 'Feito');
    } else if (filterStatus === 'feito') {
        filteredTasks = filteredTasks.filter(t => t.status === 'Feito');
    }

    // Sort by SLA date
    filteredTasks.sort((a, b) => {
        const dateA = new Date(`${a.data}T${a.horario}:00`);
        const dateB = new Date(`${b.data}T${b.horario}:00`);
        return dateA - dateB;
    });

    const isTaskLate = (task) => {
        if (task.status === 'Feito') return false;
        if (!task.data || !task.horario) return false;
        return new Date() > new Date(`${task.data}T${task.horario}:00`);
    };

    const lateTasksList = tasks.filter(t => isTaskLate(t) && !acknowledgedLateTaskIds.includes(t.id));

    const getClosestTask = (urgencyLevel) => {
        const pendingTasks = tasks.filter(t => t.urgencyLevel === urgencyLevel && t.status !== 'Feito' && t.data && t.horario);
        if (pendingTasks.length === 0) return null;
        
        // Ordenar pela data do SLA (o menor primeiro, que é o mais próximo ou o mais atrasado)
        pendingTasks.sort((a, b) => {
            return new Date(`${a.data}T${a.horario}:00`) - new Date(`${b.data}T${b.horario}:00`);
        });
        return pendingTasks[0];
    };

    const getTimeRemainingString = (task) => {
        if (!task) return '--';
        if (task.status === 'Feito') return 'Concluído';
        if (!task.data || !task.horario) return '--';
        const slaDate = new Date(`${task.data}T${task.horario}:00`);
        const diffMs = slaDate - currentTime;
        
        const isLate = diffMs <= 0;
        const absDiff = Math.abs(diffMs);
        
        const diffHrs = Math.floor(absDiff / (1000 * 60 * 60));
        const diffMins = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
        const diffSecs = Math.floor((absDiff % (1000 * 60)) / 1000);
        
        let res = isLate ? 'Atrasado ' : 'Falta ';
        if (diffHrs > 0) res += `${diffHrs}h `;
        res += `${diffMins}m ${diffSecs}s`;
        return res;
    };

    if (TAREFAS_HOMECARE_DESATIVADO) {
        return (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
                <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Tarefas Homecare desativado</h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
            </div>
        );
    }

    return (
        <div className="tarefas-homecare-container" style={{ padding: '20px', minHeight: '100vh', transition: 'background-color 0.5s' }}>
            
            {/* POPUP DE ALARME (SLA ESTOURADO) */}
            {showAlarmPopup && (
                <div className="reminder-urgent-overlay" style={{ zIndex: 9999 }}>
                    <div className="reminder-urgent-container" style={{ 
                        width: '100vw', 
                        height: '100vh', 
                        maxWidth: 'none', 
                        borderRadius: '0', 
                        margin: '0', 
                        padding: '50px 5%', 
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <div className="reminder-urgent-bar reminder-urgent-bar-top"></div>
                        <div className="reminder-urgent-bar reminder-urgent-bar-bottom"></div>
                        <div className="reminder-urgent-icon" style={{ marginTop: '20px' }}>🚨</div>
                        
                        <h1 className="reminder-urgent-title">
                            SLA ESTOURADO!
                        </h1>

                        <div className="reminder-urgent-count">
                            <span className="reminder-urgent-count-number">{lateTasksList.length}</span>
                            <span className="reminder-urgent-count-label">
                                tarefa(s) ultrapassou o tempo limite.
                            </span>
                        </div>

                        <div className="reminder-urgent-list" style={{ 
                            flexGrow: 1, 
                            maxHeight: 'none',
                            overflowY: 'auto', 
                            overflowX: 'hidden',
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
                            gap: '15px',
                            padding: '20px',
                            alignContent: 'start'
                        }}>
                            {lateTasksList.map((item) => (
                                <div key={item.id} className="reminder-urgent-item" style={{ margin: 0, padding: '12px' }}>
                                    <span className="reminder-urgent-item-time" style={{ minWidth: '80px', fontSize: '0.95rem' }}>
                                    ⏰ {item.horario} — {formatDateBR(item.data)}
                                    </span>
                                    <span className="reminder-urgent-item-text" style={{ fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.titulo}
                                    </span>
                                    <span className="reminder-urgent-item-meta" style={{ fontSize: '0.85rem', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                        👤 {getExecutorName(item.executante)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="reminder-urgent-actions" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <button className="reminder-urgent-btn-dismiss" onClick={handleDismissAlarm}>
                                ✓ Ciente (Silenciar)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ margin: 0, color: '#333' }}>Tarefas Homecare (Matriz de Prioridades)</h1>
                <button 
                    onClick={openNewModal}
                    style={{ padding: '10px 20px', backgroundColor: '#00897b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    + Nova Tarefa
                </button>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: '#c62828', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Urgente e Importante</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.urgente_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(255,255,255,0.2)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('urgente_importante'))}
                    </div>
                </div>
                <div style={{ backgroundColor: '#fbc02d', color: '#333', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Não Urgente / Importante</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.nao_urgente_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(0,0,0,0.1)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('nao_urgente_importante'))}
                    </div>
                </div>
                <div style={{ backgroundColor: '#f57c00', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Urgente / Não Importante</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.urgente_nao_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(255,255,255,0.2)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('urgente_nao_importante'))}
                    </div>
                </div>
                <div style={{ backgroundColor: '#2e7d32', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Não Urg / Não Imp</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.nao_urgente_nao_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(255,255,255,0.2)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('nao_urgente_nao_importante'))}
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Filtrar por Executante:</label>
                    <select value={filterExecutante} onChange={e => setFilterExecutante(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '200px' }}>
                        <option value="">Todos</option>
                        {systemUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Status da Tarefa:</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '150px' }}>
                        <option value="all">Todas</option>
                        <option value="pendente">Apenas Pendentes</option>
                        <option value="feito">Concluídas</option>
                    </select>
                </div>
            </div>

            {/* Lista de Tarefas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredTasks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#888', backgroundColor: '#fff', borderRadius: '8px' }}>
                        Nenhuma tarefa encontrada com os filtros atuais.
                    </div>
                ) : (
                    filteredTasks.map(task => {
                        const late = isTaskLate(task);
                        return (
                            <div key={task.id} style={{ 
                                display: 'flex', alignItems: 'center', gap: '15px', 
                                backgroundColor: '#fff', padding: '15px', borderRadius: '8px',
                                borderLeft: late ? '6px solid #dc3545' : '6px solid #28a745',
                                boxShadow: late ? '0 0 10px rgba(220,53,69,0.3)' : '0 2px 4px rgba(0,0,0,0.05)',
                                opacity: task.status === 'Feito' ? 0.7 : 1,
                                transition: 'all 0.3s'
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={task.status === 'Feito'}
                                    onChange={() => handleToggleStatus(task)}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <div style={{ flexGrow: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: task.status === 'Feito' ? '#888' : '#333', textDecoration: task.status === 'Feito' ? 'line-through' : 'none' }}>
                                        {task.titulo}
                                    </div>
                                    <div style={{ display: 'flex', gap: '20px', fontSize: '0.85rem', color: '#666', marginTop: '6px' }}>
                                        <span style={{ fontWeight: 'bold' }}>👤 {getExecutorName(task.executante)} {task.setor ? `(${task.setor})` : ''}</span>
                                        <span style={{ color: late ? '#dc3545' : '#666', fontWeight: late ? 'bold' : 'normal', backgroundColor: late ? '#ffe6e6' : 'transparent', padding: late ? '2px 6px' : '0', borderRadius: '4px', display: 'inline-block' }}>
                                            ⏰ SLA: <strong>{formatDateBR(task.data)}</strong> às <strong>{task.horario}</strong>
                                            {late && <span style={{ marginLeft: '6px', fontWeight: 'bold' }}>(EM ATRASO)</span>}
                                        </span>

                                        {/* SLA Ajustado — linha discreta */}
                                        {task.slaOriginal && (
                                            <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '3px' }}>
                                                SLA anterior: {formatDateBR(task.slaOriginal.data)} às {task.slaOriginal.horario}
                                            </div>
                                        )}
                                    </div>
                                    {task.status !== 'Feito' && (
                                        <div style={{ marginTop: '8px', fontSize: '0.8rem', fontWeight: 'bold', color: late ? '#dc3545' : '#00897b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            ⏱️ {getTimeRemainingString(task)}
                                        </div>
                                    )}

                                    {/* Comentário */}
                                    {task.comentario && (
                                        <div style={{ marginTop: '8px', padding: '6px 10px', background: '#f0faf8', borderRadius: '6px', border: '1px solid #b2dfdb', fontSize: '0.83rem', color: '#37474f' }}>
                                            💬 <em>{task.comentario}</em>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button 
                                        onClick={() => { setEditingTask(task); setIsModalOpen(true); }}
                                        style={{ padding: '6px 12px', border: '1px solid #ccc', backgroundColor: '#f8f9fa', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', color: '#555' }}
                                    >
                                        Editar
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteTask(task.id)}
                                        style={{ padding: '6px 12px', border: 'none', backgroundColor: '#dc3545', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Excluir
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <AddHomecareTaskModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSave={handleSaveTask}
                editingTask={editingTask}
                systemUsers={systemUsers}
            />
        </div>
    );
}

export default TarefasHomecare;
